import React, { useState, useRef, useEffect } from "react";
import "./Questionnaire.css";
import questionsData from "../data/questions.json";
// import questionsData from "../data/sample_questions.json";
import config from "../config";

const QUESTIONS_PER_PAGE = 10;

const Questionnaire = () => {
  const [questions] = useState(questionsData.questions);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [userEmail, setUserEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showErrors, setShowErrors] = useState(false);
  const [unansweredWarning, setUnansweredWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previousPage, setPreviousPage] = useState(1);

  const questionContainerRef = useRef(null);
  const descriptionRef = useRef(null);

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);

  useEffect(() => {
    // No initial scroll
  }, []);

  useEffect(() => {
    if (
      currentPage > 1 &&
      currentPage !== previousPage &&
      descriptionRef.current
    ) {
      descriptionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }

    setPreviousPage(currentPage);
  }, [currentPage, previousPage]);

  const handleInputChange = (
    questionNum,
    value,
    isCheckbox = false,
    questionType
  ) => {
    setErrors((prev) => ({ ...prev, [questionNum]: "" }));
    setUnansweredWarning(false);

    if (isCheckbox) {
      setAnswers((prev) => ({
        ...prev,
        [questionNum]: prev[questionNum]
          ? prev[questionNum].includes(value)
            ? prev[questionNum].filter((item) => item !== value)
            : [...prev[questionNum], value]
          : [value],
      }));
    } else {
      setAnswers((prev) => ({
        ...prev,
        [questionNum]: questionType === "text_field" ? value : [value],
      }));
    }
  };

  const handleEmailChange = (e) => {
    setUserEmail(e.target.value);
    // Clear email mismatch error when user types
    if (errors.emailMismatch) {
      setErrors((prev) => ({ ...prev, emailMismatch: "" }));
    }
  };

  const handleConfirmEmailChange = (e) => {
    setConfirmEmail(e.target.value);
    // Clear email mismatch error when user types
    if (errors.emailMismatch) {
      setErrors((prev) => ({ ...prev, emailMismatch: "" }));
    }
  };

  const validateCurrentPageAnswers = () => {
    const newErrors = {};
    let isValid = true;
    let firstUnansweredQuestion = null;

    const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
    const endIndex = startIndex + QUESTIONS_PER_PAGE;
    const pageQuestions = questions.slice(startIndex, endIndex);

    pageQuestions.forEach((question) => {
      if (
        !answers[question.question_num] ||
        (Array.isArray(answers[question.question_num]) &&
          answers[question.question_num].length === 0) ||
        (question.type === "text_field" &&
          (!answers[question.question_num] ||
            !answers[question.question_num].trim()))
      ) {
        newErrors[question.question_num] = "This question is required";
        isValid = false;
        if (!firstUnansweredQuestion) {
          firstUnansweredQuestion = question.question_num;
        }
      }
    });

    setErrors(newErrors);
    setUnansweredWarning(!isValid);

    if (!isValid && firstUnansweredQuestion) {
      document.getElementById(`q${firstUnansweredQuestion}`).scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    return isValid;
  };

  const transformAnswersToBinary = () => {
    return questions.reduce((acc, question) => {
      const userAnswer = answers[question.question_num] || [];
      const binaryArray = question.answers.map((answer) =>
        Array.isArray(userAnswer)
          ? userAnswer.includes(answer)
            ? 1
            : 0
          : userAnswer[0] === answer
          ? 1
          : 0
      );
      acc[question.question_num] = binaryArray;
      return acc;
    }, {});
  };

  const handleNextPage = () => {
    if (currentPage === 1) {
      const newErrors = {};
      
      if (!userEmail) {
        newErrors.email = "Email is required";
      }
      
      if (!confirmEmail) {
        newErrors.confirmEmail = "Please confirm your email";
      }
      
      if (userEmail && confirmEmail && userEmail !== confirmEmail) {
        newErrors.emailMismatch = "Emails do not match";
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        document.getElementById("email").scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
    }

    if (!validateCurrentPageAnswers()) {
      setShowErrors(true);
      return;
    }

    setCurrentPage((prev) => prev + 1);
    setShowErrors(false);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => prev - 1);
    setShowErrors(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateCurrentPageAnswers()) {
      return;
    }

    setIsSubmitting(true);
    const transformedAnswers = transformAnswersToBinary();

    try {
      const response = await fetch(`${config.backendUrl}/cmcp_assessment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_email: userEmail,
          answers: transformedAnswers,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        console.error("Submission failed:", await response.text());
        alert("Failed to submit questionnaire. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting answers:", error);
      alert(
        "Error submitting questionnaire. Please check your connection and try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    return (
      <React.Fragment key={question.question_num}>
        <div className="question-container" id={`q${question.question_num}`}>
          <p>
            {question.question_num}. {question.question}
          </p>
          <div className="options-container">
            {question.type === "single_choice" &&
              question.answers.map((answer, idx) => (
                <div
                  key={idx}
                  className={`option-box ${
                    answers[question.question_num]?.[0] === answer
                      ? "selected-option"
                      : ""
                  }`}
                  onClick={() =>
                    handleInputChange(
                      question.question_num,
                      answer,
                      false,
                      question.type
                    )
                  }
                >
                  {answer}
                </div>
              ))}

            {question.type === "multiple_choice" &&
              question.answers.map((answer, idx) => (
                <div
                  key={idx}
                  className={`option-box ${
                    answers[question.question_num]?.includes(answer)
                      ? "selected-option"
                      : ""
                  }`}
                  onClick={() =>
                    handleInputChange(
                      question.question_num,
                      answer,
                      true,
                      question.type
                    )
                  }
                >
                  {answer}
                </div>
              ))}

            {question.type === "text_field" && (
              <textarea
                id={`q${question.question_num}`}
                value={answers[question.question_num] || ""}
                onChange={(e) =>
                  handleInputChange(
                    question.question_num,
                    e.target.value,
                    false,
                    question.type
                  )
                }
                rows="4"
                placeholder="Enter your answer here..."
              />
            )}
          </div>
          {showErrors && errors[question.question_num] && (
            <div className="error-message">{errors[question.question_num]}</div>
          )}
        </div>
        {question.question_num === 16 && (
          <div className="info-message">
            These next questions pertain to your life before you were 16 years
            old.
          </div>
        )}
        {question.question_num === 45 && (
          <div className="info-message">
            These next questions pertain to the last 5 years.
          </div>
        )}
      </React.Fragment>
    );
  };

  if (submitted) {
    return (
      <div className="questionnaire" style={{ minHeight: "auto" }}>
        <div className="questionnaire-content" style={{ padding: "1rem" }}>
          <div className="success-message">
            Your submission is complete. Your answers will be combined with your
            genetic predispositions to generate your Emotional State Report,
            which Report will be sent to you approximately ten days after the
            lab receives your sample for analysis.
          </div>
        </div>
      </div>
    );
  }

  const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
  const endIndex = startIndex + QUESTIONS_PER_PAGE;
  const pageQuestions = questions.slice(startIndex, endIndex);

  return (
    <div className="questionnaire" ref={questionContainerRef}>
      <div className="questionnaire-content">
        <form onSubmit={handleSubmit}>
          {currentPage === 1 && (
            <>
              <div className="form-group">
                <label htmlFor="email">Email:</label>
                <input
                  type="email"
                  id="email"
                  value={userEmail}
                  onChange={handleEmailChange}
                  required
                  placeholder="Enter your email address"
                />
                {errors.email && (
                  <div className="error-message">{errors.email}</div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="confirmEmail">Confirm Email:</label>
                <input
                  type="email"
                  id="confirmEmail"
                  value={confirmEmail}
                  onChange={handleConfirmEmailChange}
                  required
                  placeholder="Re-enter your email address"
                />
                {errors.confirmEmail && (
                  <div className="error-message">{errors.confirmEmail}</div>
                )}
                {errors.emailMismatch && (
                  <div className="error-message">{errors.emailMismatch}</div>
                )}
              </div>
            </>
          )}

          <div className="description" ref={descriptionRef}>
            Please select the closest answer.
          </div>

          <div className="page-indicator">
            Page {currentPage} of {totalPages}
          </div>

          {currentPage === 6 && (
            <div className="info-message" style={{ marginBottom: "2rem" }}>
              These next questions pertain to the past year.
            </div>
          )}

          {pageQuestions.map(renderQuestion)}

          {unansweredWarning && (
            <div className="warning-message">Please answer all questions.</div>
          )}

          <div className="page-indicator">
            Page {currentPage} of {totalPages}
          </div>

          <div className="button-group">
            {currentPage > 1 && (
              <button
                type="button"
                onClick={handlePreviousPage}
                className="previous-page"
                disabled={isSubmitting}
              >
                Previous Page
              </button>
            )}
            <button
              type={currentPage === totalPages ? "submit" : "button"}
              onClick={currentPage < totalPages ? handleNextPage : handleSubmit}
              className={`next-page ${currentPage === 1 ? "centered" : ""}`}
              disabled={isSubmitting}
            >
              {currentPage < totalPages ? (
                "Next Page"
              ) : isSubmitting ? (
                <div className="button-content">
                  <div className="loading-spinner"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Questionnaire;
